<template>
  <v-col
    v-if="readyForCustomize"
    class="video-player-options py-0"
    style="margin-bottom: 15px"
  >
    <v-row>
      <v-col :data-hint="$t('message.introJS.videoPageOptions.basics')">
        <h3 class="mb-2">
          {{ $t('message.pages.video.playerOptionTitles.basic') }}
        </h3>
        <v-card flat tile class="d-flex flex-column text-left">
          <!-- colors -->
          <v-btn class="btn" x-large @click="handleSelectOption('colors')" text>
            <span class="d-flex">
              <v-icon>mdi-invert-colors</v-icon>
              <span class="optionsText">
                {{ $t('message.pages.video.colors') }}
              </span>
            </span>
            <span
              v-if="
                !checkChangesInState(
                  profile.colors.controlsColor,
                  player.config.controlsColor
                ) ||
                !checkChangesInState(
                  profile.colors.primaryColor,
                  player.config.primaryColor
                )
              "
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <!-- controls -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('controls')"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-format-list-checks</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.video.controls') }}
              </p>
            </span>
            <span
              v-if="
                !checkChangesInState(
                  profile.controls,

                  player.config.controls
                ) ||
                profile.vsl.restartAfterEnd ||
                profile.vsl.disablePause ||
                profile.hideControlsOnStart
              "
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <!-- Big play button -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('bigPlayButton')"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-play-circle-outline</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.video.bigPlayButton') }}
              </p>
            </span>
            <span
              v-if="checkBigPlayButton"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <!-- subtitles -->
          <v-tooltip
            top
            v-if="
              !video.hasOwnProperty('video_source') &&
              (video.status === 'CONVERTING' || !video.playable)
            "
          >
            <template v-slot:activator="{ on, attrs }">
              <div class="w-fill" v-on="on" v-bind="attrs">
                <v-btn
                  class="btn"
                  x-large
                  @click="handleSelectOption('subtitles')"
                  style="width: 100%"
                  text
                  :disabled="video.status === 'CONVERTING' || !video.playable"
                  v-on="on"
                  v-bind="attrs"
                >
                  <span class="d-flex">
                    <div class="container-list-icon">
                      <simple-svg
                        :src="require('@/assets/closed-captions.svg')"
                        fill-class-name="fill-color"
                        fill="#7A7A7A"
                        width="24px"
                        height="24px"
                      />
                    </div>
                    <p class="optionsText">
                      {{ $t('message.pages.videoEditModal.subtitles') }}
                      <sup class="newItemText">BETA</sup>
                    </p>
                  </span>
                  <div
                    class="d-flex justify-end align-center"
                    style="width: 100%"
                  >
                    <FlagTooltip
                      :items="subtitlesTooltipInfo"
                      :originalLang="video.original_lang || ''"
                      v-if="subtitlesTooltipInfo"
                    >
                      <FlagCards
                        :items="
                          getItemsCountryCode(
                            subtitlesTooltipInfo.subtitles.items
                          )
                        "
                      />
                    </FlagTooltip>
                    <span v-if="checkForSubtitles" class="ml-3">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div v-on="on" class="d-inline-block">
                            <span class="circle-player"></span>
                          </div>
                        </template>
                        <span
                          >{{ video.config.subtitles.length }}
                          {{
                            video.config.subtitles.length > 1
                              ? $t('message.pages.video.subtitles')
                              : $t('message.pages.video.subtitle')
                          }}</span
                        >
                      </v-tooltip>
                    </span>
                  </div>
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </template>
            <span>{{ $t('message.pages.login.warningConversion') }}</span>
          </v-tooltip>
          <v-btn
            v-else-if="!video.hasOwnProperty('video_source')"
            class="btn"
            x-large
            @click="handleSelectOption('subtitles')"
            style="width: 100%"
            text
          >
            <span class="d-flex">
              <div class="container-list-icon">
                <simple-svg
                  :src="require('@/assets/closed-captions.svg')"
                  fill-class-name="fill-color"
                  :fill="$vuetify.theme.dark ? 'white' : 'black'"
                  width="24px"
                  height="24px"
                />
              </div>
              <p class="optionsText">
                {{ $t('message.pages.videoEditModal.subtitles') }}
                <sup class="newItemText">BETA</sup>
              </p>
            </span>
            <div class="d-flex justify-end align-center" style="width: 100%">
              <FlagTooltip
                :items="subtitlesTooltipInfo"
                :originalLang="video.original_lang || ''"
                v-if="subtitlesTooltipInfo"
              >
                <FlagCards
                  :items="
                    getItemsCountryCode(subtitlesTooltipInfo.subtitles.items)
                  "
                />
              </FlagTooltip>
              <span v-if="checkForSubtitles" class="ml-3">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <div v-on="on" class="d-inline-block">
                      <span class="circle-player"></span>
                    </div>
                  </template>
                  <span
                    >{{ video.config.subtitles.length }}
                    {{
                      video.config.subtitles.length > 1
                        ? $t('message.pages.video.subtitles')
                        : $t('message.pages.video.subtitle')
                    }}</span
                  >
                </v-tooltip>
              </span>
            </div>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <portal-target
            id="tooltip-portal"
            name="flagTooltipPortal"
          ></portal-target>

          <!-- dubbing -->
          <v-tooltip
            top
            v-if="
              !video.hasOwnProperty('video_source') &&
              (video.status === 'CONVERTING' || !video.playable)
            "
          >
            <template
              v-slot:activator="{ on, attrs }"
              v-if="
                !video.hasOwnProperty('video_source') &&
                (video.status === 'CONVERTING' || !video.playable)
              "
            >
              <div class="w-fill" v-on="on" v-bind="attrs">
                <v-btn
                  class="btn"
                  x-large
                  @click="handleSelectOption('dubbing')"
                  style="width: 100%"
                  text
                  :disabled="video.status === 'CONVERTING' || !video.playable"
                >
                  <span class="d-flex">
                    <div class="container-list-icon">
                      <simple-svg
                        :src="require('@/assets/dubbing.svg')"
                        fill-class-name="fill-color"
                        fill="#7A7A7A"
                        width="24px"
                        height="24px"
                      />
                    </div>
                    <p class="optionsText">
                      {{ $i18next.t('message.ai.dubbings.dubbing') }}
                      <sup class="newItemText">BETA</sup>
                    </p>
                  </span>
                  <div
                    v-if="activeAudios && activeAudios.length"
                    class="d-flex justify-end align-center"
                    style="width: 100%"
                  >
                    <FlagTooltip
                      :items="audiosTooltipInfo"
                      :originalLang="video.original_lang || ''"
                      v-if="audiosTooltipInfo"
                    >
                      <FlagCards
                        :items="
                          getItemsCountryCode(audiosTooltipInfo.audios.items)
                        "
                      />
                    </FlagTooltip>
                    <span v-if="activeAudios" class="ml-3">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div v-on="on" class="d-inline-block">
                            <span class="circle-player"></span>
                          </div>
                        </template>
                        <span
                          >{{ activeAudios.length }}
                          {{
                            activeAudios.length > 1
                              ? $i18next.t('message.ai.dubbings.dubbings')
                              : $i18next.t('message.ai.dubbings.dubbing')
                          }}</span
                        >
                      </v-tooltip>
                    </span>
                  </div>
                  <v-icon color="">mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </template>
            <span>{{ $t('message.pages.login.warningConversion') }}</span>
          </v-tooltip>
          <v-btn
            v-else-if="!video.hasOwnProperty('video_source')"
            class="btn"
            x-large
            @click="handleSelectOption('dubbing')"
            style="width: 100%"
            text
            :disabled="video.status === 'CONVERTING' || !video.playable"
          >
            <span class="d-flex">
              <div class="container-list-icon">
                <simple-svg
                  :src="require('@/assets/dubbing.svg')"
                  fill-class-name="fill-color"
                  :fill="$vuetify.theme.dark ? 'white' : 'black'"
                  width="24px"
                  height="24px"
                />
              </div>
              <p class="optionsText">
                {{ $i18next.t('message.ai.dubbings.dubbing') }}
                <sup class="newItemText">BETA</sup>
              </p>
            </span>
            <div
              v-if="activeAudios && activeAudios.length"
              class="d-flex justify-end align-center"
              style="width: 100%"
            >
              <FlagTooltip
                :items="audiosTooltipInfo"
                :originalLang="video.original_lang || ''"
                v-if="audiosTooltipInfo"
              >
                <FlagCards
                  :items="getItemsCountryCode(audiosTooltipInfo.audios.items)"
                />
              </FlagTooltip>
              <span v-if="activeAudios" class="ml-3">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <div v-on="on" class="d-inline-block">
                      <span class="circle-player"></span>
                    </div>
                  </template>
                  <span
                    >{{ activeAudios.length }}
                    {{
                      activeAudios.length > 1
                        ? $i18next.t('message.ai.dubbings.dubbings')
                        : $i18next.t('message.ai.dubbings.dubbing')
                    }}</span
                  >
                </v-tooltip>
              </span>
            </div>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <!-- thumbnails -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('thumbnails')"
            :disabled="!canShowTimeOptions"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-folder-multiple-image</v-icon>
              <p class="optionsText">
                Thumbnails
                <sup class="newItemText">{{ $t('message.newItem') }}</sup>
              </p>
            </span>
            <span
              v-if="!isBunnyThumbnail() || havePauseThumbnail().length !== 0"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <div>
              <v-progress-circular
                v-if="!canShowTimeOptions"
                size="22"
                width="2"
                indeterminate
                color="primary"
              />
              <v-icon>mdi-chevron-right</v-icon>
            </div>
          </v-btn>

          <!-- chapters -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('chapters')"
            :disabled="!canShowTimeOptions"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-view-list</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.video.chapters') }}
              </p>
            </span>
            <div
              v-if="video.config.bookMarks"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span
                v-if="
                  !video.config.bookMarks || video.config.bookMarks.length !== 0
                "
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <div v-on="on" class="d-inline-block">
                      <span class="circle-player"></span>
                    </div>
                  </template>
                  <span
                    >{{ video.config.bookMarks.length }}
                    {{
                      video.config.bookMarks.length > 1
                        ? $t('message.pages.video.chapters')
                        : $t('message.pages.video.chapter')
                    }}</span
                  >
                </v-tooltip>
              </span>
            </div>
            <div>
              <v-progress-circular
                v-if="!canShowTimeOptions"
                size="22"
                width="2"
                indeterminate
                color="primary"
              />
              <v-icon>mdi-chevron-right</v-icon>
            </div>
          </v-btn>

          <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-btn
                  class="btn"
                  x-large
                  text
                  :disabled="true"
                  style="width: 100%"
                >
                  <span class="d-flex">
                    <v-icon>mdi-comment-text-outline</v-icon>
                    <p class="optionsText">
                      {{ $t('message.pages.videoEditModal.subtitles') }}
                    </p>
                  </span>
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </template>
            <span>{{
              $t('message.pages.externalSourceVideo.unavailableFor')
            }}</span>
          </v-tooltip>
        </v-card>
      </v-col>
    </v-row>
    <v-row no-gutters class="mt-2" v-if="canSeePlanInfo">
      <options-ai
        :isBeta="isBeta"
        :handleSelectOption="handleSelectOption"
      ></options-ai>
    </v-row>
    <v-row no-gutters class="mt-2">
      <v-col :data-hint="$t('message.introJS.videoPageOptions.advanced')">
        <h3 class="mb-2">
          {{ $t('message.pages.video.playerOptionTitles.advanced') }}
        </h3>
        <v-card flat tile class="d-flex flex-column text-left">
          <!-- mini hook -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('minitrigger')"
            :disabled="!canShowTimeOptions"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-hook</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.video.miniTrigger.miniTrigger') }}
                <sup class="newItemText">{{ $t('message.newItem') }}</sup>
              </p>
            </span>
            <span
              v-if="Boolean(hasMiniTriggers())"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <div>
              <v-progress-circular
                v-if="!canShowTimeOptions"
                size="22"
                width="2"
                indeterminate
                color="primary"
              />
              <v-icon>mdi-chevron-right</v-icon>
            </div>
          </v-btn>

          <!-- autoplay -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('autoplay')"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-play-box-outline</v-icon>
              <p class="optionsText">
                <span class="mr-1">Smart Autoplay</span>
                <sup class="newItemText">{{ $t('message.newItem') }}</sup>
              </p>
            </span>
            <span
              v-if="profile.vsl.autoplay || profile.vsl.mutedIndicatorIcon"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <!-- alternativeProgress -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('alternativeProgress')"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-clock-start</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.video.alternativeProgress') }}
              </p>
            </span>
            <span
              v-if="profile.vsl.alternativeProgress"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <!-- showPreviewThumbs -->
          <v-btn
            v-if="!video.hasOwnProperty('video_source')"
            class="btn"
            x-large
            text
            @click="handleSelectOption('showPreviewThumbs')"
          >
            <span class="d-flex">
              <v-icon>mdi-tooltip-image</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.video.showPreviewThumbs') }}
              </p>
            </span>
            <span
              v-if="profile.general.showPreviewThumbs"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <!-- saveProgress -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('saveProgress')"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-backup-restore</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.video.saveProgress') }}
              </p>
            </span>
            <span
              v-if="
                !checkChangesInState(
                  profile.saveProgress.saveProgress,
                  player.config.saveProgress
                ) || profile.saveProgress.saveProgressScreen
              "
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <!-- speed -->
          <template>
            <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-btn
                    class="btn"
                    x-large
                    @click="handleSelectOption('speed')"
                    :disabled="video.hasOwnProperty('video_source')"
                    text
                    style="width: 100%"
                  >
                    <span class="d-flex">
                      <div class="">
                        <simple-svg
                          :src="require('@/assets/icon-video-speed.svg')"
                          fill-class-name="fill-color"
                          :fill="$vuetify.theme.dark ? 'white' : 'black'"
                          width="24px"
                          height="36px"
                        />
                      </div>
                      <p class="optionsText" style="align-self: center">
                        {{ $i18next.t('message.pages.videoSpeed.videoSpeed') }}
                      </p>
                    </span>

                    <div>
                      <v-progress-circular
                        v-if="video.status != 'CONVERTED' && video.playable"
                        size="22"
                        width="2"
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                      <v-icon>mdi-chevron-right</v-icon>
                    </div>
                  </v-btn>
                </div>
              </template>
              <span>{{
                $t('message.pages.externalSourceVideo.unavailableFor')
              }}</span>
            </v-tooltip>
            <v-btn
              v-else
              class="btn"
              x-large
              @click="handleSelectOption('speed')"
              text
            >
              <span class="d-flex">
                <div class="">
                  <simple-svg
                    :src="require('@/assets/icon-video-speed.svg')"
                    fill-class-name="fill-color"
                    :fill="$vuetify.theme.dark ? 'white' : 'black'"
                    width="24px"
                    height="24px"
                  />
                </div>
                <p class="optionsText">
                  {{ $i18next.t('message.pages.videoSpeed.videoSpeed') }}
                </p>
              </span>
              <div>
                <v-progress-circular
                  v-if="video.status != 'CONVERTED' && video.playable"
                  size="22"
                  width="2"
                  indeterminate
                  color="primary"
                />
                <v-icon>mdi-chevron-right</v-icon>
              </div>
            </v-btn>
          </template>
          <!-- affiliate -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('affiliate')"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-bullseye-arrow</v-icon>
              <p class="optionsText">{{ $t('message.pandaVideoAffiliate') }}</p>
            </span>
            <span
              v-if="
                !checkChangesInState(
                  profile.vsl.pandaBranding,

                  player.config.pandaBranding
                ) ||
                !checkChangesInState(profile.vsl.refCode, player.config.refCode)
              "
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <!-- Pixels -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('pixels')"
            text
            :disabled="!canShowTimeOptions"
          >
            <span class="d-flex">
              <v-icon>mdi-xml</v-icon>
              <p class="optionsText">
                Pixels
                <sup class="newItemText">{{ $t('message.newItem') }}</sup>
              </p>
            </span>
            <span
              v-if="hasPixels"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span class="circle-player"></span>
            </span>
            <div>
              <v-progress-circular
                v-if="!canShowTimeOptions"
                size="22"
                width="2"
                indeterminate
                color="primary"
              />
              <v-icon>mdi-chevron-right</v-icon>
            </div>
          </v-btn>

          <!-- buttons -->
          <v-btn
            class="btn"
            x-large
            @click="handleSelectOption('buttons')"
            :disabled="!canShowTimeOptions"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-gesture-tap-button</v-icon>
              <p class="optionsText">{{ $t('message.pages.video.buttons') }}</p>
            </span>
            <div
              v-if="video.config.ctas"
              class="d-flex"
              style="width: 100%; justify-content: right"
            >
              <span v-if="!haveButtons() || haveButtons().length !== 0">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <div v-on="on" class="d-inline-block">
                      <span class="circle-player"></span>
                    </div>
                  </template>
                  <span
                    >{{ haveButtons().length }}
                    {{
                      haveButtons().length > 1
                        ? $t('message.pages.video.buttons')
                        : $t('message.pages.video.button')
                    }}</span
                  >
                </v-tooltip>
              </span>
            </div>
            <div>
              <v-progress-circular
                v-if="!canShowTimeOptions"
                size="22"
                width="2"
                indeterminate
                color="primary"
              />
              <v-icon>mdi-chevron-right</v-icon>
            </div>
          </v-btn>

          <!-- showPreviewThumbs -->
          <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-btn
                  class="btn"
                  x-large
                  :disabled="video.hasOwnProperty('video_source')"
                  text
                  style="width: 100%"
                >
                  <span class="d-flex">
                    <v-icon>mdi-tooltip-image</v-icon>
                    <p class="optionsText">
                      {{ $t('message.pages.video.showPreviewThumbs') }}
                    </p>
                  </span>
                  <div>
                    <v-progress-circular
                      v-if="video.status != 'CONVERTED' && video.playable"
                      size="22"
                      width="2"
                      indeterminate
                      color="primary"
                    />
                    <v-icon>mdi-chevron-right</v-icon>
                  </div>
                </v-btn>
              </div>
            </template>
            <span>{{
              $t('message.pages.externalSourceVideo.unavailableFor')
            }}</span>
          </v-tooltip>

          <!-- quality -->
          <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-btn
                  class="btn"
                  x-large
                  @click="handleSelectOption('quality')"
                  :disabled="video.hasOwnProperty('video_source')"
                  text
                  style="width: 100%"
                >
                  <span class="d-flex">
                    <v-icon>mdi-quality-high</v-icon>
                    <p class="optionsText">
                      {{ $t('message.pages.video.qualities') }}
                    </p>
                  </span>
                  <div>
                    <v-progress-circular
                      v-if="video.status != 'CONVERTED' && video.playable"
                      size="22"
                      width="2"
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                    <v-icon>mdi-chevron-right</v-icon>
                  </div>
                </v-btn>
              </div>
            </template>
            <span>{{
              $t('message.pages.externalSourceVideo.unavailableFor')
            }}</span>
          </v-tooltip>
          <v-btn
            v-else
            class="btn"
            x-large
            @click="handleSelectOption('quality')"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-quality-high</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.video.qualities') }}
              </p>
            </span>
            <div>
              <v-progress-circular
                v-if="video.status != 'CONVERTED' && video.playable"
                size="22"
                width="2"
                indeterminate
                color="primary"
              />
              <v-icon>mdi-chevron-right</v-icon>
            </div>
          </v-btn>

          <!-- drm :: security -->
          <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-btn
                  class="btn"
                  x-large
                  :disabled="video.hasOwnProperty('video_source')"
                  text
                  style="width: 100%"
                >
                  <span class="d-flex">
                    <v-icon>mdi-security</v-icon>
                    <p class="optionsText">
                      {{ $t('message.pages.drm.watermark') }}
                    </p>
                  </span>
                  <div>
                    <v-progress-circular
                      v-if="video.status != 'CONVERTED' && video.playable"
                      size="22"
                      width="2"
                      indeterminate
                      color="primary"
                    />
                    <v-icon>mdi-chevron-right</v-icon>
                  </div>
                </v-btn>
              </div>
            </template>
            <span>{{
              $t('message.pages.externalSourceVideo.unavailableFor')
            }}</span>
          </v-tooltip>
          <v-btn
            v-else
            class="btn"
            x-large
            @click="handleSelectOption('drmSecurity')"
            text
          >
            <span class="d-flex">
              <v-icon>mdi-security</v-icon>
              <p class="optionsText">
                {{ $t('message.pages.drm.watermark') }}
              </p>
            </span>
            <div>
              <v-progress-circular
                v-if="video.status != 'CONVERTED' && video.playable"
                size="22"
                width="2"
                indeterminate
                color="primary"
              />
              <v-icon>mdi-chevron-right</v-icon>
            </div>
          </v-btn>
        </v-card>
      </v-col>
    </v-row>
  </v-col>
  <v-col
    v-else
    class="pa-0 d-flex justify-center align-center"
    style="width: 100%; height: 100%"
  >
    <span v-if="!video.id">{{
      $t('message.pages.externalSourceVideo.notFoundVideo')
    }}</span>
    <v-progress-circular v-else indeterminate color="primary" />
  </v-col>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import _ from 'lodash'
import colorHelpers from '@/utils/colorHelpers'
import i18n from '@/translations/lang'
import introMixin from '@/utils/intro.mixin.js'
import FlagTooltip from '@/components/FlagTooltip.vue'
import FlagCards from '@/components/FlagCards.vue'
import langCodes from '@/utils/langCodes.mixin.js'
import optionsAi from './videoPlayerOptionsAI/VideoPlayerOptionsAI.vue'

export default {
  name: 'VideoPlayerOptions',
  mixins: [introMixin, langCodes],
  components: { FlagTooltip, FlagCards, optionsAi },
  inject: ['introPageKey'],
  props: {
    handleSelectOption: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    profileId: null,
    readyForCustomize: false,
    isBeta: false,
    isExternalTimeLoaded: false,
  }),
  created() {
    if (this.$route.name !== 'NewLive') {
      if (!this.video.config?.bookMarks) this.video.config.bookMarks = []
      if (!this.video.config?.subtitles) this.video.config.subtitles = []
      if (!this.video?.audios) this.video.audios = []
      this.UPDATE_VIDEO(this.video)
    }
  },
  async mounted() {
    this.$root.$on('updatedExternalTimeVideo', () => {
      this.isExternalTimeLoaded = true
    })
    this.$root.$on('fetchSubtitles', async () => {
      await this.fetchSubtitles({
        libraryId: this.video.pullzone_name,
        videoId: this.video.video_external_id,
      })
    })
    this.isBeta = location.search.match('beta')
    this.$store.state.video.player?.config ? '' : await this.getVideoPlayer()
    if (this.video.config) this.checkProfile()
    this.haveButtons()
  },
  computed: {
    ...mapGetters('video', ['activeAudios']),
    ...mapGetters('user', ['canSeePlanInfo']),
    ...mapState({
      video: (state) => state.video.video,
      player: (state) => state.video.player,
      profile: (state) => state.video.profile,
      profile_id: (state) => state.video.profile_id,
    }),
    checkForSubtitles() {
      return this.video.config?.subtitles?.length > 0
    },
    subtitlesTooltipInfo() {
      if (
        this.video?.config?.subtitles &&
        this.video?.config?.subtitles?.length
      ) {
        return {
          subtitles: {
            label: this.$i18next.t('message.ai.subtitles.subtitle'),
            iconFile: 'cc_tooltip',
            items: this.video.config.subtitles.map((item) =>
              this.getPreparedSrcLang(item.srclang)
            ),
          },
        }
      }
      return false
    },
    audiosTooltipInfo() {
      if (this.activeAudios && this.activeAudios?.length) {
        return {
          audios: {
            label: this.$i18next.t('message.ai.dubbings.dubbing'),
            iconFile: 'dubbing',
            items: this.activeAudios.map((item) =>
              this.getPreparedSrcLang(item.lang)
            ),
          },
        }
      }
      return false
    },
    canShowTimeOptions() {
      if (
        this.video.hasOwnProperty('video_source') &&
        !this.isExternalTimeLoaded &&
        !this.video.length
      ) {
        return false
      }
      return true
    },
    hasEditDrmPerm() {
      return (
        (this.$store.state.user?.userPermissions?.includes('put-video') &&
          this.$store.state.user?.userPermissions?.includes('put-watermark')) ??
        false
      )
    },
    hasPixels() {
      if (
        !this.checkChangesInState(
          this.profile.vsl.fbPixel,
          this.player.config.fbPixel
        ) ||
        !this.checkChangesInState(
          this.profile.vsl.gtagIds,
          this.player.config.gtagIds
        ) ||
        (this.video.config.customPixels &&
          this.video.config?.customPixels.length)
      )
        return true
      return false
    },
    checkBigPlayButton() {
      if (this.profile.controls.indexOf('play-large') === -1) {
        return true
      } else if (
        this.profile?.general.bigPlayButtonSize !== 150 ||
        this.profile?.general.bigPlayButtonIconSize !== 86
      ) {
        return true
      } else {
        return false
      }
    },
  },
  watch: {
    'video.config': function () {
      this.checkProfile()
    },
    intro_status(value) {
      const key = 'video-page-options'

      const hasFinished = value === 'finished'
      const isVideoPage = this.intro_key === this.introPageKey
      const isVideoOptions = this.intro_key === key

      if (hasFinished && isVideoPage) {
        this.setIntroJS({
          key,
          delay: 350,
          type: 'hint',
          reset: true,
        })
        setTimeout(() => {
          this.intro_key = this.introPageKey
          this.intro_type = 'intro'
        }, 400)
      }

      if (hasFinished && isVideoOptions) {
        this.intro_key = this.introPageKey
        this.intro_type = 'intro'
      }
    },
  },
  beforeDestroy() {
    this.intro_key = this.introPageKey
    this.intro_type = 'intro'
    this.intro_status = 'finished'
    this.$root.$off('fetchSubtitles')

    this.hideIntroJS()
  },
  methods: {
    ...mapActions('video', [
      'getVideoPlayer',
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
      'fetchSubtitles',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_VIDEO',
      'UPDATE_VIDEO_PROFILE',
    ]),

    getItemsCountryCode(items) {
      const finalItems = items.map((item) => this.getCountryCodeBySrcLang(item))
      return finalItems
    },
    async checkProfile() {
      const addOpacity = (color, opacity = 'B3') =>
        `${color}${opacity}`.slice(0, 9)

      this.readyForCustomize = true
      this.profileId = this.video.config?.profile

      if (!this.profileId) {
        this.video.config['profile'] = this.profileId
      }

      const profile = {
        colors: {
          captionsColor:
            this.profile.colors?.captionsColor ??
            this.player.config?.captionsColor,
          captionsBackgroundColor:
            this.profile.colors?.captionsBackgroundColor ??
            this.player.config?.captionsBackgroundColor,
          controlsColor: this.profile.colors?.controlsColor
            ? this.profile.colors?.controlsColor
            : this.player.config?.controlsColor,
          primaryColor: this.profile.colors?.primaryColor
            ? this.profile.colors?.primaryColor
            : this.player.config?.primaryColor,
        },
        general: {
          bigPlayButtonSize: this.profile.general?.bigPlayButtonSize
            ? this.profile.general?.bigPlayButtonSize
            : 100,
          bigPlayButtonIconSize: this.profile.general?.bigPlayButtonIconSize
            ? this.profile.general?.bigPlayButtonIconSize
            : 57,
          showPreviewThumbs: this.profile.general?.showPreviewThumbs ?? true,
          captionsFontSize: this.profile.general?.captionsFontSize
            ? this.profile.general?.captionsFontSize
            : this.player.config?.captionsFontSize,
          startsWithSubtitle: this.profile.general?.startsWithSubtitle
            ? this.profile.general?.startsWithSubtitle
            : false,
        },
        controls: this.profile?.controls
          ? this.profile?.controls
          : this.player.config.controls,
        hideControlsOnStart: this.profile?.hideControlsOnStart
          ? this.profile?.hideControlsOnStart
          : false,
        saveProgress: {
          initQuality: this.profile.saveProgress?.initQuality
            ? this.profile.saveProgress?.initQuality
            : undefined,
          saveProgress:
            this.profile.saveProgress?.saveProgress !==
              this.player.config.saveProgress &&
            this.profile.saveProgress?.saveProgress !== undefined
              ? this.profile.saveProgress?.saveProgress
              : this.player.config.saveProgress,
          saveProgressScreen: this.profile.saveProgress?.saveProgressScreen
            ? this.profile.saveProgress?.saveProgressScreen
            : false,
          saveProgressTitle: this.profile.saveProgress?.saveProgressTitle
            ? this.profile.saveProgress?.saveProgressTitle
            : i18n.t(
                'message.pages.video.titleInputsResumePlay.titlePlaceholder'
              ),
          saveProgressBackgroundOpacity: this.profile.saveProgress
            ?.saveProgressBackgroundOpacity
            ? this.profile.saveProgress?.saveProgressBackgroundOpacity
            : 0.8,
          saveProgressBackgroundOpacityFormated:
            this.profile.saveProgress?.saveProgressBackgroundOpacity * 100
              ? this.profile.saveProgress?.saveProgressBackgroundOpacity * 100
              : 80,
          saveProgressButton1Title: this.profile.saveProgress
            ?.saveProgressButton1Title
            ? this.profile.saveProgress?.saveProgressButton1Title
            : i18n.t(
                'message.pages.video.titleInputsResumePlay.playButtonPlaceholder'
              ),
          saveProgressButton2Title: this.profile.saveProgress
            ?.saveProgressButton2Title
            ? this.profile.saveProgress?.saveProgressButton2Title
            : i18n.t(
                'message.pages.video.titleInputsResumePlay.restartButtonPlaceholder'
              ),
        },
        updated_at: this.profile.updated_at
          ? this.profile.updated_at
          : undefined,
        vsl: {
          alternativeProgress: this.profile.vsl?.alternativeProgress
            ? this.profile.vsl?.alternativeProgress
            : false,
          alternativeProgress2xLimit: this.profile.vsl
            ?.alternativeProgress2xLimit
            ? this.profile.vsl?.alternativeProgress2xLimit
            : 50,
          alternativeProgressHeight: this.profile.vsl?.alternativeProgressHeight
            ? this.profile.vsl.alternativeProgressHeight
            : 10,
          alternativeProgressVelocity: this.profile.vsl
            ?.alternativeProgressVelocity
            ? this.profile.vsl?.alternativeProgressVelocity
            : 2,
          alternativeProgressDefaultVelocity: this.profile.vsl
            ?.alternativeProgressDefaultVelocity
            ? this.profile.vsl?.alternativeProgressDefaultVelocity
            : false,
          autoplay: this.profile.vsl?.autoplay
            ? this.profile.vsl?.autoplay
            : false,
          mutedIndicatorIcon: this.profile.vsl?.mutedIndicatorIcon
            ? this.profile.vsl?.mutedIndicatorIcon
            : false,
          mutedIndicatorFontSize: this.profile.vsl?.mutedIndicatorFontSize
            ? this.profile.vsl?.mutedIndicatorFontSize
            : 22,
          mutedIndicatorTextTop: this.profile.vsl?.mutedIndicatorTextTop
            ? this.profile.vsl?.mutedIndicatorTextTop
            : i18n.t('message.pages.video.mutedIndicator.topTextDefault'),
          mutedIndicatorTextBottom: this.profile.vsl?.mutedIndicatorTextBottom
            ? this.profile.vsl?.mutedIndicatorTextBottom
            : i18n.t('message.pages.video.mutedIndicator.bottomTextDefault'),
          mutedIndicatorBackgroundColor: this.profile.vsl
            ?.mutedIndicatorBackgroundColor
            ? this.profile.vsl?.mutedIndicatorBackgroundColor
            : colorHelpers.hexToRgba(
                addOpacity(this.player.config?.primaryColor)
              ),
          mutedIndicatorTextColor: this.profile.vsl?.mutedIndicatorTextColor
            ? this.profile.vsl?.mutedIndicatorTextColor
            : colorHelpers.hexToRgba(
                addOpacity(this.player.config?.controlsColor, 'ff')
              ),
          restartAfterEnd: this.profile.vsl?.restartAfterEnd
            ? this.profile.vsl?.restartAfterEnd
            : false,
          disablePause: this.profile.vsl?.disablePause
            ? this.profile.vsl?.disablePause
            : false,
          disableForward: this.profile.vsl?.disableForward
            ? this.profile.vsl?.disableForward
            : false,
          refCode:
            this.profile.vsl?.refCode !== this.player.config.refCode &&
            this.profile.vsl?.refCode !== undefined
              ? this.profile?.vsl.refCode
              : this.player.config.refCode,
          fbPixel:
            this.profile.vsl?.fbPixel !== this.player.config.fbPixel &&
            this.profile.vsl?.fbPixel !== undefined
              ? this.profile?.vsl.fbPixel
              : this.player.config.fbPixel,
          gtagIds:
            this.profile.vsl?.gtagIds !== this.player.config.gtagIds &&
            this.profile.vsl?.gtagIds !== undefined
              ? this.profile?.vsl.gtagIds
              : this.player.config.gtagIds,
          pandaBranding:
            this.profile.vsl?.pandaBranding !==
              this.player.config.pandaBranding &&
            this.profile.vsl?.pandaBranding !== undefined
              ? this.profile.vsl?.pandaBranding
              : this.player.config.pandaBranding,
          mutedIndicatorAnimation: this.profile.vsl?.mutedIndicatorAnimation
            ? this.profile.vsl?.mutedIndicatorAnimation
            : this.profile.vsl?.mutedIndicatorIcon
            ? 'default'
            : 'impact',
          customMutedIndicatorId:
            this.profile.vsl?.customMutedIndicatorId || null,
          mutedIndicatorLoop: this.profile.vsl?.mutedIndicatorLoop || false,
          mutedIndicatorLoopDuration:
            this.profile.vsl?.mutedIndicatorLoopDuration || 15,
          smartAutoplay: this.profile.vsl?.smartAutoplay || false,
          mutedIndicatorSize: this.profile.vsl?.mutedIndicatorSize || 'small',
          mutedIndicatorBorderSize:
            this.profile.vsl?.mutedIndicatorBorderSize || 2,
          defaultLanguage: this.profile.vsl?.defaultLanguage,
          smallMutedIcon: this.profile.vsl?.smallMutedIcon ?? true,
        },
      }

      if (
        this.video.hasOwnProperty('video_source') &&
        !this.video.config.profile
      ) {
        profile.vsl.smartAutoplay =
          profile.vsl.autoplay =
          profile.vsl.mutedIndicatorIcon =
          profile.vsl.alternativeProgress =
            true
        profile.vsl.mutedIndicatorAnimation = 'impact'

        this.UPDATE_VIDEO_PROFILE(profile)
        await this.createProfile(this.video.id)
        this.video.config.profile = this.profile_id
        this.profileId = this.profile_id
        await this.setProfileToVideos([this.profile_id, this.video.id])
        await this.updateProfile([this.profile_id, profile])
      } else {
        this.UPDATE_VIDEO_PROFILE(profile)
      }
    },
    checkChangesInState(currentState, defaultState) {
      return _.isEqual(currentState, defaultState)
    },
    isBunnyThumbnail() {
      return [this.video.thumbnail || []].includes('thumbnail.jpg')
    },
    havePauseThumbnail() {
      let arrayCtas = this.$store.state.video.video.config?.ctas
      if (!arrayCtas) {
        return (arrayCtas = [])
      }
      return arrayCtas.filter((item) => !item.title)
    },
    haveButtons() {
      let arrayCtas = this.$store.state.video.video.config?.ctas
      let outsideCta = this.$store.state.video.video.config?.outsideCta
      if (!arrayCtas) {
        arrayCtas = []
      }
      if (!outsideCta) {
        outsideCta = []
      }
      let arrayCtasFiltered = arrayCtas.filter((item) => item.title)
      return arrayCtasFiltered.concat(outsideCta)
    },
    hasMiniTriggers() {
      let arrayCtas = this.$store.state.video.video.config?.ctas
      if (!arrayCtas) {
        arrayCtas = []
      }
      let arrayCtasFiltered = arrayCtas.filter(
        (item) => item.metadata?.isMiniTrigger
      )
      return arrayCtasFiltered?.length
    },
  },
}
</script>

<style>
.circle-player {
  margin-right: 5px;
  margin-top: -2px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: rgb(30 216 231);
  display: inline-block;
}
</style>

<style lang="scss" scoped>
.video-player-options {
  .btn {
    justify-content: space-between;
    text-transform: unset;
  }
}

.optionsText {
  font-weight: 400;
  margin: 0 0 0 5px !important;
  letter-spacing: normal;
  padding: 2px 0;
}

.container-list-icon {
  max-width: 24px;
  max-height: 24px;
}

.newSectionStyle {
  background-color: #ebedff;
  color: var(--v-primary-base);
  font-weight: 500;
  text-transform: uppercase;
  width: 68px;
  display: flex;
  border-radius: 24px;
}
.newSectionStyle span {
  margin: 0 auto;
}
</style>
